import React, { useState, useEffect } from 'react';
import { useStores, put, remove, generateId, getAll } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { Plus, Trash2, Edit2, X, Paperclip, Wallet, ArrowDownRight, ArrowUpRight, CheckCircle, AlertTriangle, FileText, Inbox, Loader2 } from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const CATEGORIES = ['Venue', 'Catering', 'Accommodation', 'Transport', 'AV & Tech', 'Gifts & Swag', 'Printing', 'Entertainment', 'Misc'];

function Modal({ entry, onSave, onClose }) {
  const [form, setForm] = useState(entry || {
    title: '',
    category: 'Misc',
    type: 'expense',
    amount: '',
    estimated: '',
    vendor: '',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
    status: 'pending',
    assignedAttendeeIds: [],
    costCenter: '',
    approver: '',
    paymentMethod: 'bank-transfer',
    attachments: []
  });
  const [attendees, setAttendees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getAll('attendees').then(setAttendees);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addAttachment = async (files) => {
    const list = Array.from(files || []);
    const oversized = list.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length) {
      setError(`Files must be under ${MAX_FILE_SIZE_MB}MB. Rejected: ${oversized.map((f) => f.name).join(', ')}`);
      return;
    }
    const valid = list.filter((file) => file.type.startsWith('image/') || file.type === 'application/pdf');

    const encoded = await Promise.all(valid.map((file) => (
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
          id: generateId(),
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl: reader.result,
          uploadedAt: new Date().toISOString()
        });
        reader.readAsDataURL(file);
      })
    )));

    setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...encoded] }));
  };

  const removeAttachment = (id) => {
    setForm((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((a) => a.id !== id) }));
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{entry ? 'Modify Ledger Entry' : 'Create Financial Record'}</h3>
            <p className="text-xs text-slate-500 mt-1">Record purchases, deposits, allocations and invoice files</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Transaction Title *</label>
            <input 
              value={form.title} 
              onChange={(e) => set('title', e.target.value)} 
              placeholder="e.g. Catering Dinner Buffet Deposit" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Record Type</label>
              <select 
                value={form.type} 
                onChange={(e) => set('type', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                <option value="expense">Expense (Debit Out)</option>
                <option value="income">Income (Credit In)</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Financial Category</label>
              <select 
                value={form.category} 
                onChange={(e) => set('category', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-display">Actual Paid Amount (INR)</label>
              <input 
                type="number" 
                value={form.amount} 
                onChange={(e) => set('amount', e.target.value)} 
                placeholder="25000" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none transition font-semibold"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Estimated Budgeted (INR)</label>
              <input 
                type="number" 
                value={form.estimated} 
                onChange={(e) => set('estimated', e.target.value)} 
                placeholder="20000" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none transition"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Vendor / Payee Agency</label>
              <input 
                value={form.vendor} 
                onChange={(e) => set('vendor', e.target.value)} 
                placeholder="Hyatt Grand Ballroom" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none transition"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Filing Date</label>
              <input 
                type="date" 
                value={form.date} 
                onChange={(e) => set('date', e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outlook-none"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Approval Payout Status</label>
            <select 
              value={form.status} 
              onChange={(e) => set('status', e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
            >
              {['pending', 'approved', 'paid', 'disputed', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Cost Center Core</label>
              <input 
                value={form.costCenter || ''} 
                onChange={(e) => set('costCenter', e.target.value)} 
                placeholder="Marketing FY25" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Authorized Approver</label>
              <input 
                value={form.approver || ''} 
                onChange={(e) => set('approver', e.target.value)} 
                placeholder="Finance Desk" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Payment Channel</label>
              <select 
                value={form.paymentMethod || 'bank-transfer'} 
                onChange={(e) => set('paymentMethod', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {['bank-transfer', 'card', 'cash', 'upi', 'wire', 'other'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Assigned Offsite Attendee</label>
              <select
                value={form.assignedAttendeeIds?.[0] || ''}
                onChange={(e) => set('assignedAttendeeIds', e.target.value ? [e.target.value] : [])}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                <option value="">General / Shared Payout</option>
                {attendees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-1 bg-slate-50 border border-slate-150 p-4 rounded-xl">
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Receipt & Docket Attachments *</label>
            <input 
              type="file" 
              accept="application/pdf,image/*" 
              multiple 
              onChange={(e) => addAttachment(e.target.files)} 
              className="text-xs mt-1.5"
            />
            {form.attachments?.length > 0 && (
              <div className="flex flex-col gap-2 mt-3">
                {form.attachments.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 border border-slate-200 bg-white rounded-lg text-xs font-medium">
                    <span className="text-slate-600 flex items-center gap-1.5"><Paperclip size={12} className="text-slate-400" /> {file.name}</span>
                    <button className="px-2 py-1 text-[10px] bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 transition" type="button" onClick={() => removeAttachment(file.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Internal Remarks</label>
            <textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Invoice credentials, bank verification keys, audit trails..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-white">
          {error && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-60"
              onClick={async () => {
                if (!form.title.trim()) { setError('Transaction title is required.'); return; }
                if (form.amount !== '' && Number(form.amount) < 0) { setError('Amount cannot be negative.'); return; }
                if (form.estimated !== '' && Number(form.estimated) < 0) { setError('Estimated amount cannot be negative.'); return; }
                setSaving(true);
                setError('');
                try { await onSave(form); } catch { setError('Failed to save. Please try again.'); setSaving(false); }
              }}
              disabled={saving}
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {entry ? 'Commit Record' : 'Record Payout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const statusClass = { 
  pending: 'sh-badge-warning', 
  approved: 'sh-badge-info', 
  paid: 'sh-badge-success', 
  disputed: 'sh-badge-danger animate-pulse', 
  cancelled: 'sh-badge-outline' 
};

const BUDGET_STORES = ['budget', 'attendees'];

export default function Budget() {
  const { budget: entries, attendees, loading } = useStores(BUDGET_STORES);
  const [modal, setModal] = useState(null);
  const [catFilter, setCatFilter] = useState('all');

  const save = async (form) => {
    await put('budget', { ...form, id: form.id || generateId() });
    setModal(null);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
    toast.success(form.id ? 'Entry updated.' : 'Entry recorded.');
  };

  const del = async (id) => {
    const ok = await confirm('This will permanently erase this financial transaction. Are you sure?', 'Delete Entry');
    if (ok) {
      try {
        await remove('budget', id);
        window.dispatchEvent(new CustomEvent('planner:data-change'));
        toast.success('Entry deleted.');
      } catch {
        toast.error('Failed to delete entry.');
      }
    }
  };

  const expenses = entries.filter((e) => e.type === 'expense');
  const income = entries.filter((e) => e.type === 'income');
  const totalExpense = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalEstimated = expenses.reduce((s, e) => s + (parseFloat(e.estimated) || parseFloat(e.amount) || 0), 0);
  const totalIncome = income.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const invoiceCoverage = expenses.length ? Math.round((expenses.filter((e) => (e.attachments || []).length > 0).length / expenses.length) * 100) : 0;

  const attendeeExpenses = expenses
    .filter((e) => e.assignedAttendeeIds?.length)
    .reduce((map, item) => {
      const attendeeId = item.assignedAttendeeIds[0];
      const amount = parseFloat(item.amount) || 0;
      map[attendeeId] = (map[attendeeId] || 0) + amount;
      return map;
    }, {});

  const byCategory = CATEGORIES.map((cat) => {
    const items = expenses.filter((e) => e.category === cat);
    return { cat, total: items.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0), count: items.length };
  }).filter((c) => c.count > 0).sort((a, b) => b.total - a.total);

  const filtered = catFilter === 'all' ? entries : entries.filter((e) => e.category === catFilter);

  if (loading) return <PageLoader label="Loading budget…" sublabel="Fetching receipts and expenses" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Wallet style={{ color: 'var(--accent)' }} size={24} />
            <span>Offsite Budget & Receipts</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Maintain corporate deposits, attendee invoice reimbursement claims, and financial projections
          </p>
        </div>
        <button 
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm" 
          onClick={() => setModal('new')}
        >
          <Plus size={16} />
          <span>Record Payout/Income</span>
        </button>
      </div>

      {/* Corporate Financial Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="sh-stat-card border-l-4 border-red-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Outflow (Debit)</span>
            <ArrowDownRight className="text-red-500" size={16} />
          </div>
          <div className="sh-stat-value text-slate-900">₹{totalExpense.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500">{expenses.length} offsite debits allocated</p>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Estimated/Projected</span>
            <AlertTriangle className="text-slate-400" size={16} />
          </div>
          <div className="sh-stat-value text-slate-700">₹{totalEstimated.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500 block">
            {totalEstimated > 0 && (
              <span className={`font-semibold ${totalExpense > totalEstimated ? 'text-red-600' : 'text-emerald-700'}`}>
                {totalExpense > totalEstimated ? '🚨 Deficit' : '✅ Saved'} by ₹{Math.abs(totalExpense - totalEstimated).toLocaleString('en-IN')}
              </span>
            )}
          </p>
        </div>

        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label font-display">Capital Pool (Credit)</span>
            <ArrowUpRight className="text-emerald-600" size={16} />
          </div>
          <div className="sh-stat-value text-slate-900">₹{totalIncome.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500">{income.length} verified income channels</p>
        </div>

        <div className="sh-stat-card border-l-4 border-sky-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Verified Receipt proofs</span>
            <CheckCircle className="text-sky-500" size={16} />
          </div>
          <div className="sh-stat-value text-slate-900">{invoiceCoverage}%</div>
          <p className="text-xs text-slate-500">Expenses containing uploaded bills</p>
        </div>
      </div>

      {/* Two column visual stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Spend by Category progress deck */}
        {byCategory.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider pb-2 border-b border-slate-100">
              Spend Allocation by Department Index
            </h3>
            <div className="space-y-4">
              {byCategory.map(({ cat, total }) => {
                const pct = totalExpense > 0 ? Math.round((total / totalExpense) * 100) : 0;
                return (
                  <div key={cat} className="space-y-1">
                    <div className="flex justify-between items-center text-xs font-semibold">
                      <span className="text-slate-700">{cat}</span>
                      <span style={{ color: 'var(--accent)' }} className="font-mono">
                        ₹{total.toLocaleString('en-IN')} ({pct}%)
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-slate-800 rounded-full" 
                        style={{ width: `${pct}%`, backgroundColor: 'var(--accent)' }} 
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Individual Attendee Reimbursement claims */}
        {Object.keys(attendeeExpenses).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider pb-2 border-b border-slate-100">
              Personal Representative Expenditures (Reimbursement claims)
            </h3>
            <div className="space-y-3 overflow-y-auto max-h-[178px] divide-y divide-slate-100">
              {Object.entries(attendeeExpenses)
                .sort((a, b) => b[1] - a[1])
                .map(([id, value]) => {
                  const person = attendees.find((a) => a.id === id);
                  return (
                    <div key={id} className="flex justify-between items-center text-xs py-2">
                      <div className="space-y-0.5">
                        <strong className="text-slate-850 block">{person?.name || 'Shared / General'}</strong>
                        <span className="text-[10px] text-slate-500 block uppercase font-bold">{person?.role || 'Offsite Participant'}</span>
                      </div>
                      <span className="font-mono font-bold text-slate-900 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
                        ₹{value.toLocaleString('en-IN')}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* category selectors */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 select-none">
          <button 
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition shrink-0 ${catFilter === 'all' ? 'bg-slate-900 text-white border-slate-900 animate-none' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} 
            onClick={() => setCatFilter('all')}
          >
            All Ledger Records ({entries.length})
          </button>
          {CATEGORIES.map((c) => (
            <button 
              key={c} 
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition shrink-0 capitalize ${catFilter === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} 
              onClick={() => setCatFilter(c)}
            >
              {c} ({entries.filter(e => e.category === c).length})
            </button>
          ))}
        </div>
      </div>

      {/* Ledger lists */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No financial ledger rows found</h3>
          <p className="text-sm text-slate-500 mt-1">Change category or insert a new credit/debit record into the Ledger</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-sm">
          <div className="table-wrap">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Filing Item</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Sector</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left w-20">Type</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Actual Spent</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Budgeted (Estimated)</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left w-28">Status</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Docket / proof</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Claim Assignee</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Filing Date</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((e) => {
                  const over = e.estimated && parseFloat(e.amount) > parseFloat(e.estimated);
                  const attendeeName = e.assignedAttendeeIds?.length ? attendees.find((a) => a.id === e.assignedAttendeeIds[0])?.name : '';
                  return (
                    <tr key={e.id} className="hover:bg-slate-50/20 transition">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{e.title}</div>
                        {e.vendor && <div className="text-xs text-slate-500">{e.vendor}</div>}
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs font-semibold">{e.category}</td>
                      <td className="py-3 px-4">
                        <span className={`sh-badge ${e.type === 'income' ? 'sh-badge-success' : 'sh-badge-danger'} font-bold py-0.5 px-2`}>
                          {e.type === 'income' ? 'credit' : 'debit'}
                        </span>
                      </td>
                      <td className={`py-3 px-4 font-mono font-bold ${e.type === 'income' ? 'text-emerald-700' : 'text-slate-900'}`}>
                        {e.amount ? `₹${Number(e.amount).toLocaleString('en-IN')}` : '-'}
                      </td>
                      <td className={`py-3 px-4 font-mono ${over ? 'text-red-750 font-bold' : 'text-slate-500'}`}>
                        {e.estimated ? `₹${Number(e.estimated).toLocaleString('en-IN')}` : '-'}
                        {over && <span className="text-red-500 ml-1 font-bold" title="Deficit: Surpassed proposed budget allocation">⚠️</span>}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`sh-badge ${statusClass[e.status] || 'sh-badge-outline'}`}>{e.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        {(e.attachments || []).length > 0 ? (
                          // One pill per document — a claim can carry several
                          // and only the first used to be reachable here.
                          <div className="flex flex-wrap gap-1">
                            {e.attachments.filter((a) => a && a.dataUrl).map((att, i) => (
                              <a
                                key={att.id || i}
                                href={att.dataUrl}
                                download={att.name || `docket-${i + 1}`}
                                title={att.name || `Docket ${i + 1}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100 transition"
                              >
                                <FileText size={11} />
                                <span>{e.attachments.length > 1 ? `Docket ${i + 1}` : 'Docket'}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Missing bill</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs font-bold">{attendeeName || 'Central shared'}</td>
                      <td className="py-3 px-4 text-slate-500 font-mono text-xs">{e.date || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button 
                            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition" 
                            onClick={() => setModal(e)}
                          >
                            <Edit2 size={13} />
                          </button>
                          <button 
                            className="p-1.5 text-red-550 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition" 
                            onClick={() => del(e.id)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && <Modal entry={modal === 'new' ? null : modal} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
}